/**
 * @component ry-avatar
 * @category  display
 * @summary   Avatar — image, initials, or icon fallback in a shape (circle, square, rounded) and size scale. Conceptually a specialised `<ry-image>`: it renders an `<img>` when `[image]` is set, falls back to `[initials]` text, and finally a default `<ry-icon name="user">` if nothing is provided. Image load failure transparently falls back to the next level so an avatar always renders SOMETHING.
 *
 * Ported from Shoelace's `<sl-avatar>` (MIT). Visual variants align with
 * daisyUI's `avatar` component (MIT). See LICENSE-THIRD-PARTY.md.
 *
 * Upstream reference:
 *   https://raw.githubusercontent.com/shoelace-style/shoelace/next/src/components/avatar/avatar.component.ts
 *   https://shoelace.style/components/avatar
 *   https://daisyui.com/components/avatar/
 *
 * @attribute image     string?                                            Image URL. When set, renders an `<img>`. On load error, transparently falls back to `[initials]` or the default user icon and emits `ry:error`.
 * @attribute label     string?                                            Accessible label. Becomes the `<img>`'s `alt`; when only initials/icon are shown, the host gains `role="img"` + `aria-label`.
 * @attribute initials  string?                                            Short text rendered when no `[image]` is set (or when the image fails to load). Typically 1-2 characters.
 * @attribute loading   'eager' | 'lazy'  default="eager"                  Maps to the inner `<img>`'s `loading` attribute.
 * @attribute shape     'circle' | 'square' | 'rounded'  default="circle"  Visual shape via border-radius.
 * @attribute size      'xs' | 'sm' | 'md' | 'lg' | 'xl'  default="md"     Preset square dimensions.
 * @attribute status    'online' | 'offline' | 'away' | 'busy' ?           Optional corner status dot (daisyUI parity). When set, a small dot is rendered in the bottom-right with the matching status color.
 *
 * @event ry:error
 *        detail: { error: Event }
 *        fires:  when the inner `<img>` fails to load. The avatar transparently falls back to initials (if provided) or the default user icon BEFORE this event is dispatched, so listeners can ignore it for visual purposes.
 *
 * @state `data-ry-state`: `"image"` (showing image), `"initials"` (showing text), `"icon"` (showing default user icon).
 *
 * @content None — `<ry-avatar>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom (image variant)
 *   ry-avatar[image][label?][shape][size][data-ry-state="image"]
 *     > img.ry-avatar__image     [data-ry-target="image"]    src=image alt=label
 *     > span.ry-avatar__status   [data-ry-target="status"]   (only when [status])
 *
 * @dom (initials variant — host gets role=img + aria-label)
 *   ry-avatar[initials="JD"][role="img"][aria-label=label]
 *     > span.ry-avatar__initials [data-ry-target="initials"] "JD"
 *
 * @dom (icon fallback — host gets role=img + aria-label)
 *   ry-avatar[role="img"][aria-label=label]
 *     > ry-icon.ry-avatar__icon  [data-ry-target="icon"]  name="user"
 *
 * @gotcha  `[image]` failure is silent visually — the component falls back to initials/icon before `ry:error` fires so the avatar never shows a broken image glyph. Listen for `ry:error` only if you need to *react* to the failure (analytics, logging).
 *
 * @gotcha  `role="img"` + `aria-label` are applied on the HOST only when no image is present. When an image renders, the `<img alt>` already does the job and adding a host role would double-announce.
 *
 * @gotcha  In happy-dom / Vitest, no real network request happens — to test the error path, dispatch an `Event('error')` on the inner img directly. The image-load happy-path is observable by reading the host's `data-ry-state`.
 *
 * @example basic-initials
 * ```html
 * <ry-avatar initials="JD" label="Jane Doe"></ry-avatar>
 * ```
 *
 * @example image
 * ```html
 * <ry-avatar image="https://i.pravatar.cc/80?img=11" label="Profile photo"></ry-avatar>
 * ```
 *
 * @example default-icon-fallback
 * ```html
 * <ry-avatar label="Unknown user"></ry-avatar>
 * ```
 *
 * @example shape-circle
 * ```html
 * <ry-avatar initials="C" shape="circle" label="Circle"></ry-avatar>
 * ```
 *
 * @example shape-square
 * ```html
 * <ry-avatar initials="S" shape="square" label="Square"></ry-avatar>
 * ```
 *
 * @example shape-rounded
 * ```html
 * <ry-avatar initials="R" shape="rounded" label="Rounded"></ry-avatar>
 * ```
 *
 * @example size-xs
 * ```html
 * <ry-avatar initials="X" size="xs" label="XS"></ry-avatar>
 * ```
 *
 * @example size-sm
 * ```html
 * <ry-avatar initials="S" size="sm" label="Small"></ry-avatar>
 * ```
 *
 * @example size-md
 * ```html
 * <ry-avatar initials="M" size="md" label="Medium"></ry-avatar>
 * ```
 *
 * @example size-lg
 * ```html
 * <ry-avatar initials="L" size="lg" label="Large"></ry-avatar>
 * ```
 *
 * @example size-xl
 * ```html
 * <ry-avatar initials="XL" size="xl" label="Extra large"></ry-avatar>
 * ```
 *
 * @example with-status-online
 * ```html
 * <ry-avatar initials="JD" status="online" label="Jane Doe (online)"></ry-avatar>
 * ```
 *
 * @example with-status-busy
 * ```html
 * <ry-avatar initials="JD" status="busy" label="Jane Doe (busy)"></ry-avatar>
 * ```
 *
 * @tutorial image-renders-img
 * ```js
 * const el = mount('<ry-avatar image="/me.jpg" label="Me"></ry-avatar>');
 * const img = el.querySelector('[data-ry-target="image"]');
 * await expectEqual(!!img, true, 'inner img exists');
 * await expectAttr(img, 'src', '/me.jpg');
 * await expectAttr(img, 'alt', 'Me');
 * await expectAttr(el, 'data-ry-state', 'image');
 * ```
 *
 * @tutorial initials-when-no-image
 * ```js
 * const el = mount('<ry-avatar initials="JD" label="Jane Doe"></ry-avatar>');
 * const initials = el.querySelector('[data-ry-target="initials"]');
 * await expectEqual(initials?.textContent, 'JD');
 * await expectAttr(el, 'data-ry-state', 'initials');
 * await expectAttr(el, 'role', 'img');
 * await expectAttr(el, 'aria-label', 'Jane Doe');
 * ```
 *
 * @tutorial default-icon-when-nothing
 * ```js
 * // Wrong assumption: an empty avatar renders empty. It always falls back to
 * // the user icon so the layout slot stays the right size.
 * const el = mount('<ry-avatar label="Unknown"></ry-avatar>');
 * const icon = el.querySelector('[data-ry-target="icon"]');
 * await expectEqual(!!icon, true);
 * await expectAttr(el, 'data-ry-state', 'icon');
 * ```
 *
 * @tutorial image-error-falls-back-to-initials
 * ```js
 * const el = mount('<ry-avatar image="/bad.jpg" initials="JD" label="Jane"></ry-avatar>');
 * const img = el.querySelector('[data-ry-target="image"]');
 * const fired = [];
 * el.addEventListener('ry:error', (e) => fired.push(e.detail));
 * img.dispatchEvent(new Event('error'));
 * await expectAttr(el, 'data-ry-state', 'initials', 'state switched to initials');
 * await expectEqual(el.querySelector('[data-ry-target="initials"]')?.textContent, 'JD');
 * await expectEqual(fired.length, 1, 'ry:error fired');
 * ```
 *
 * @tutorial image-error-falls-back-to-icon-when-no-initials
 * ```js
 * const el = mount('<ry-avatar image="/bad.jpg" label="x"></ry-avatar>');
 * const img = el.querySelector('[data-ry-target="image"]');
 * img.dispatchEvent(new Event('error'));
 * await expectAttr(el, 'data-ry-state', 'icon');
 * await expectEqual(!!el.querySelector('[data-ry-target="icon"]'), true);
 * ```
 *
 * @tutorial shape-and-size-attrs-applied
 * ```js
 * const el = mount('<ry-avatar initials="A" shape="rounded" size="lg" label="A"></ry-avatar>');
 * await expectAttr(el, 'shape', 'rounded');
 * await expectAttr(el, 'size', 'lg');
 * ```
 *
 * @tutorial status-dot-renders-when-present
 * ```js
 * const el = mount('<ry-avatar initials="JD" status="online" label="Jane"></ry-avatar>');
 * const dot = el.querySelector('[data-ry-target="status"]');
 * await expectEqual(!!dot, true, 'status dot rendered');
 * await expectAttr(el, 'status', 'online');
 * ```
 *
 * @tutorial no-status-no-dot
 * ```js
 * const el = mount('<ry-avatar initials="JD" label="Jane"></ry-avatar>');
 * await expectEqual(el.querySelector('[data-ry-target="status"]'), null);
 * ```
 *
 * @see ry-image
 * @see ry-icon
 */

import { RyElement } from '../core/ry-element.js';

export type RyAvatarShape = 'circle' | 'square' | 'rounded';
export type RyAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type RyAvatarStatus = 'online' | 'offline' | 'away' | 'busy';

interface AvatarErrorDetail {
  error: Event;
}

export class RyAvatar extends RyElement {
  static get observedAttributes(): string[] {
    return ['image', 'label', 'initials', 'loading', 'shape', 'size', 'status'];
  }

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (this.hasAttribute('data-ry-initialized')) this.#render();
  }

  #render(): void {
    const image = this.getAttribute('image');
    const label = this.getAttribute('label') ?? '';
    const initials = this.getAttribute('initials');
    const loading = this.getAttribute('loading') ?? 'eager';
    const status = this.getAttribute('status');

    // Decide which slot fills the avatar (image > initials > icon).
    const mode: 'image' | 'initials' | 'icon' = image
      ? 'image'
      : initials
        ? 'initials'
        : 'icon';

    this.dataset.ryState = mode;

    // Host-level ARIA: image variant relies on the inner <img alt>; non-image
    // variants need role + aria-label so screen readers announce the avatar.
    if (mode === 'image') {
      this.removeAttribute('role');
      this.removeAttribute('aria-label');
    } else {
      this.setAttribute('role', 'img');
      if (label) {
        this.setAttribute('aria-label', label);
      } else {
        this.removeAttribute('aria-label');
      }
    }

    const statusMarkup = status
      ? `<span data-ry-target="status" class="ry-avatar__status ry-avatar__status--${escapeAttr(status)}"></span>`
      : '';

    let inner = '';
    if (mode === 'image') {
      inner = `<img
        data-ry-target="image"
        class="ry-avatar__image"
        src="${escapeAttr(image ?? '')}"
        alt="${escapeAttr(label)}"
        loading="${escapeAttr(loading)}"
      />`;
    } else if (mode === 'initials') {
      inner = `<span
        data-ry-target="initials"
        class="ry-avatar__initials"
        aria-hidden="true"
      >${escapeText(initials ?? '')}</span>`;
    } else {
      inner = `<ry-icon
        data-ry-target="icon"
        class="ry-avatar__icon"
        name="user"
        aria-hidden="true"
      ></ry-icon>`;
    }

    this.innerHTML = `${inner}${statusMarkup}`;

    if (mode === 'image') {
      const img = this.$<HTMLImageElement>('[data-ry-target="image"]');
      if (img) {
        this.on(img, 'error', (e: Event) => this.#handleImageError(e));
      }
    }
  }

  #handleImageError(e: Event): void {
    // Transparent fallback: clear [image] so the next #render picks the
    // initials/icon path. attributeChangedCallback re-renders before ry:error
    // fires so listeners never observe a broken-image state.
    this.removeAttribute('image');
    if (this.dataset.ryState === 'image') {
      // Defensive: attributeChangedCallback only runs after the first connect;
      // if a test mutated the DOM before init, render manually.
      this.#render();
    }
    this.emit<AvatarErrorDetail>('error', { error: e });
  }
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

customElements.define('ry-avatar', RyAvatar);

declare global {
  interface HTMLElementTagNameMap {
    'ry-avatar': RyAvatar;
  }
}
